import { PrintExampleDto, StepDto } from '@/api-sdk';
import { ValidationConfiguratorContextType } from '@/context';

type UpdateValidationConfiguratorContext = {
  validationConfiguratorContext: ValidationConfiguratorContextType;
  step?: {
    name: StepDto['type'] | string;
    value: boolean;
  };
  showValidationSnackbar?: ValidationConfiguratorContextType['showValidationSnackbar'];
};

/**
 * Updates the validation configurator context with the provided values.
 *
 * This function takes an object containing the current validation configurator context,
 * an optional step object, and an optional showValidationSnackbar flag. It returns a new
 * validation configurator context object with the updated values.
 *
 * If the showValidationSnackbar flag is provided, it updates the showValidationSnackbar
 * property in the context. If the step object is provided, it updates the showValidationMessages
 * property in the context with the step's name and value.
 *
 * @param validationConfiguratorContext - The current validation configurator context.
 * @param step - An optional object containing the step's name and value.
 * @param showValidationSnackbar - An optional flag to show the validation snackbar.
 * @returns The updated validation configurator context.
 */
export function updateValidationConfiguratorContext({
  validationConfiguratorContext,
  step,
  showValidationSnackbar,
}: UpdateValidationConfiguratorContext): ValidationConfiguratorContextType {
  return {
    ...validationConfiguratorContext,
    ...(showValidationSnackbar !== undefined && { showValidationSnackbar }),
    ...(step && {
      showValidationMessages: {
        ...validationConfiguratorContext.showValidationMessages,
        [step.name]: step.value,
      },
    }),
  };
}

/**
 * Type guard to check if PrintExampleDto has all required string properties for PrintExampleType.
 * Validates that filename and url are both non-null strings.
 */
export function isPrintExampleType(
  printExampleData: PrintExampleDto | null | undefined,
): printExampleData is { uuid: string; filename: string; url: string } {
  return Boolean(
    printExampleData &&
      typeof printExampleData.filename === 'string' &&
      typeof printExampleData.url === 'string',
  );
}
